/**
 * IMA ADPCM decoder for the backend's voice-call speech, ported from the PiPar
 * app (lib/services/adpcm_codec_service.dart).
 *
 * The stream (Rust TTS sidecar, or encode_pcm_to_adpcm_stream in the backend):
 * each sentence starts with a 60-byte WAV header, then 1024-byte mono blocks of
 * 24 kHz audio - a 4-byte preamble (first sample, step index) and 2040 bytes of
 * 4-bit samples, low nibble first. Every block stands on its own.
 */

const WAV_HEADER_SIZE = 60
const BLOCK_SIZE = 1024

const STEP_TABLE = [
  7, 8, 9, 10, 11, 12, 13, 14, 16, 17, 19, 21, 23, 25, 28, 31,
  34, 37, 41, 45, 50, 55, 60, 66, 73, 80, 88, 97, 107, 118, 130,
  143, 157, 173, 190, 209, 230, 253, 279, 307, 337, 371, 408, 449,
  494, 544, 598, 658, 724, 796, 876, 963, 1060, 1166, 1282, 1411,
  1552, 1707, 1878, 2066, 2272, 2499, 2749, 3024, 3327, 3660, 4026,
  4428, 4871, 5358, 5894, 6484, 7132, 7845, 8630, 9493, 10442, 11487,
  12635, 13899, 15289, 16818, 18500, 20350, 22385, 24623, 27086, 29794,
  32767,
]
const INDEX_TABLE = [-1, -1, -1, -1, 2, 4, 6, 8, -1, -1, -1, -1, 2, 4, 6, 8]

const isRiff = (b: Uint8Array) => b.length >= 4 && b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46

export class AdpcmDecoder {
  private buffer = new Uint8Array(0)
  private headerSkipped = false

  /** Decodes the whole blocks received so far to 16-bit samples (null when none
   *  is complete yet); a partial block waits for the next chunk. */
  decode(chunk: Uint8Array): Int16Array | null {
    // A new WAV header: the next sentence. Drop any partial block of the last.
    if (isRiff(chunk)) {
      this.buffer = new Uint8Array(0)
      this.headerSkipped = false
    }
    const joined = new Uint8Array(this.buffer.length + chunk.length)
    joined.set(this.buffer)
    joined.set(chunk, this.buffer.length)
    let data = joined

    if (!this.headerSkipped) {
      if (data.length < WAV_HEADER_SIZE) {
        this.buffer = data
        return null
      }
      // No header: the data is raw ADPCM blocks.
      if (isRiff(data)) data = data.subarray(WAV_HEADER_SIZE)
      this.headerSkipped = true
    }

    const blocks = Math.floor(data.length / BLOCK_SIZE)
    this.buffer = data.slice(blocks * BLOCK_SIZE)
    if (!blocks) return null
    const out = new Int16Array(blocks * ((BLOCK_SIZE - 4) * 2 + 1))
    let n = 0
    for (let b = 0; b < blocks; b++) n = decodeBlock(data.subarray(b * BLOCK_SIZE, (b + 1) * BLOCK_SIZE), out, n)
    return out
  }
}

function decodeBlock(block: Uint8Array, out: Int16Array, n: number): number {
  // Preamble: first sample (i16 LE), step index (u8), reserved (u8)
  let predictor = block[0] | (block[1] << 8)
  if (predictor > 32767) predictor -= 65536
  let stepIndex = Math.min(88, block[2])
  out[n++] = predictor
  for (let i = 4; i < block.length; i++) {
    for (const nibble of [block[i] & 0x0f, (block[i] >> 4) & 0x0f]) {
      const step = STEP_TABLE[stepIndex]
      let diff = step >> 3
      if (nibble & 4) diff += step
      if (nibble & 2) diff += step >> 1
      if (nibble & 1) diff += step >> 2
      if (nibble & 8) diff = -diff
      predictor = Math.max(-32768, Math.min(32767, predictor + diff))
      stepIndex = Math.max(0, Math.min(88, stepIndex + INDEX_TABLE[nibble]))
      out[n++] = predictor
    }
  }
  return n
}
